import { Link, useLocation } from "@tanstack/react-router";
import { LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { BrandLogo } from "@/components/BrandLogo";
import { ConnectionDropAlert } from "@/components/connections/ConnectionDropAlert";
import { NotificationBell } from "@/components/notifications/NotificationInbox";
import { IntegrationAlerts } from "@/components/notifications/IntegrationAlerts";
import { CommandPalette, CommandPaletteTrigger } from "@/components/CommandPalette";
import { navEntries, type NavItem } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, type ReactNode } from "react";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = getInitials(user?.email);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const content = contentRef.current;

    if (!content) return undefined;

    if (mobileOpen) {
      content.setAttribute("inert", "");
    } else {
      content.removeAttribute("inert");
    }

    return () => {
      content.removeAttribute("inert");
    };
  }, [mobileOpen]);

  const handleSignOut = (): void => {
    setMobileOpen(false);
    void signOut();
  };

  return (
    <div className="flex h-screen h-[100dvh] overflow-hidden bg-background text-foreground">
      <aside className="hidden h-full w-64 shrink-0 border-r border-border bg-surface md:flex">
        <SidebarContent
          currentPath={location.pathname}
          email={user?.email}
          initials={initials}
          onSignOut={handleSignOut}
        />
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {mobileOpen && (
        <aside
          className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[calc(100vw-2rem)] animate-slide-in border-r border-border bg-surface shadow-lg md:hidden"
          aria-label="Menu lateral"
          aria-modal="true"
          role="dialog"
        >
          <SidebarContent
            currentPath={location.pathname}
            email={user?.email}
            initials={initials}
            onNavigate={() => setMobileOpen(false)}
            onSignOut={handleSignOut}
            headerAction={
              <IconButton
                label="Fechar menu"
                onClick={() => setMobileOpen(false)}
              >
                <X size={18} />
              </IconButton>
            }
          />
        </aside>
      )}

      <div
        ref={contentRef}
        className="flex min-w-0 flex-1 flex-col"
        aria-hidden={mobileOpen ? true : undefined}
      >
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-3 md:hidden">
          <IconButton
            label="Abrir menu"
            onClick={() => setMobileOpen(true)}
            expanded={mobileOpen}
          >
            <Menu size={20} />
          </IconButton>

          <Brand compact />

          <div className="flex items-center gap-2">
            <NotificationBell />
            <UserAvatar initials={initials} />
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          <div className="mb-4 empty:hidden">
            <ConnectionDropAlert />
          </div>
          <div className="mb-4 empty:hidden">
            <IntegrationAlerts />
          </div>
          {children}
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}

interface SidebarContentProps {
  currentPath: string;
  email?: string | undefined;
  initials: string;
  headerAction?: ReactNode;
  onNavigate?: (() => void) | undefined;
  onSignOut: () => void;
}

function SidebarContent({
  currentPath,
  email,
  initials,
  headerAction,
  onNavigate,
  onSignOut,
}: SidebarContentProps): JSX.Element {
  return (
    <div className="flex min-h-0 w-full flex-col">
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
        <Brand />
        <div className="flex items-center gap-2">
          {headerAction ?? <NotificationBell />}
        </div>
      </div>

      <div className="px-3 pt-3">
        <CommandPaletteTrigger />
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4" aria-label="Menu principal">
        <div className="space-y-1">
          {navEntries.map((entry) => {
            if ("type" in entry && entry.type === "section") {
              const isSectionActive = entry.children.some((child) =>
                isActiveRoute(currentPath, child.to),
              );

              return (
                <div key={entry.label} className="space-y-0.5">
                  <p
                    className={cn(
                      "px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em]",
                      isSectionActive
                        ? "text-foreground"
                        : "text-muted-foreground/60",
                    )}
                  >
                    {entry.label}
                  </p>
                  {entry.children.map((child) => (
                    <NavLink
                      key={child.to}
                      item={child}
                      isActive={isActiveRoute(currentPath, child.to)}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              );
            }

            const item = entry as NavItem;
            return (
              <NavLink
                key={item.to}
                item={item}
                isActive={isActiveRoute(currentPath, item.to)}
                onNavigate={onNavigate}
              />
            );
          })}
        </div>
      </nav>

      <div className="shrink-0 border-t border-border p-3">
        <div className="rounded-lg border border-border/80 bg-background/35 p-2">
          <div className="flex min-w-0 items-center gap-3 px-1.5 py-1.5">
            <UserAvatar initials={initials} />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground">
                {email ?? "Conta LucraLink"}
              </p>
              <p className="text-[11px] font-medium text-muted-foreground">
                Painel ativo
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onSignOut}
            className={cn(
              "mt-2 flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium",
              "text-muted-foreground transition-colors hover:bg-surface-raised hover:text-destructive",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <LogOut size={16} className="shrink-0" />
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  onNavigate?: (() => void) | undefined;
}

function NavLink({ item, isActive, onNavigate }: NavLinkProps): JSX.Element {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "group relative flex h-9 min-w-0 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-all duration-150 ease-[cubic-bezier(0.16,1,0.3,1)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isActive
          ? "bg-[hsl(var(--surface-raised))] text-foreground font-semibold border border-[hsl(var(--border-strong))] shadow-[0_1px_2px_rgba(0,0,0,0.5),_inset_0_1px_0_0_rgba(255,255,255,0.06)]"
          : "text-muted-foreground hover:bg-[hsl(var(--surface-raised)/0.6)] hover:text-foreground",
      )}
      aria-current={isActive ? "page" : undefined}
    >
      <span
        className={cn(
          "absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-full transition-all duration-150",
          isActive ? "bg-primary opacity-100 shadow-[0_0_8px_rgba(16,185,129,0.6)]" : "opacity-0",
        )}
        aria-hidden="true"
      />
      <Icon
        size={16}
        className={cn(
          "shrink-0 transition-all duration-150",
          isActive ? "text-primary sidebar-icon-active" : "text-muted-foreground group-hover:text-foreground",
        )}
        aria-hidden="true"
      />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

interface BrandProps {
  compact?: boolean;
}

function Brand({ compact = false }: BrandProps): JSX.Element {
  return <BrandLogo compact={compact} />;
}

interface IconButtonProps {
  children: ReactNode;
  expanded?: boolean;
  label: string;
  onClick: () => void;
}

function IconButton({ children, expanded, label, onClick }: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised",
        "text-muted-foreground transition-colors hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
      aria-expanded={expanded}
      aria-label={label}
    >
      {children}
    </button>
  );
}

interface UserAvatarProps {
  initials: string;
}

function UserAvatar({ initials }: UserAvatarProps): JSX.Element {
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10"
      aria-hidden="true"
    >
      <span className="font-mono text-[10px] font-bold uppercase text-primary">
        {initials}
      </span>
    </div>
  );
}

function isActiveRoute(currentPath: string, targetPath: string): boolean {
  const normalizedPath = currentPath.replace(/\/+$/, "") || "/";
  const normalizedTarget = targetPath.replace(/\/+$/, "") || "/";

  // Rotas com subrota propria no menu (ex.: /app/links/massa) casam so exato.
  if (normalizedTarget === "/app" || normalizedTarget === "/app/links" || normalizedTarget === "/app/analytics") {
    return normalizedPath === normalizedTarget;
  }

  return normalizedPath === normalizedTarget || normalizedPath.startsWith(`${normalizedTarget}/`);
}

function getInitials(email?: string): string {
  const localPart = email?.split("@")[0]?.trim();

  if (!localPart) {
    return "LL";
  }

  return localPart.slice(0, 2).toUpperCase();
}
